import Decimal from 'decimal.js';
import rfdc from 'rfdc';

export const mapObjectKeysToValues = <O extends Record<string, any>>(object: O) =>
    Object.fromEntries(Object.keys(object).map((key) => [key, key])) as { [K in keyof O]: K };

export const mapObjectValues = <O extends Record<string, any>, R>(
    object: O,
    callback: ([key, value]: [keyof O, O[keyof O]], currentObject: O) => R,
) =>
    Object.fromEntries(
        Object.entries(object || {}).map(([key, value]) => [key, callback([key, value], object)]),
    ) as Record<keyof O, R>;

export const stabilizeNullFields = <O extends Record<string, any>>(object: O) =>
    Object.fromEntries(Object.entries(object).map(([key, value]) => [key, value === null ? undefined : value])) as {
        [K in keyof O]: null extends O[K] ? Exclude<O[K], null> | undefined : O[K];
    };

export function objectToJsonValue<O extends Record<string, any>>(
    object: O,
): {
    [K in keyof O]: O[K] extends unknown
        ? any
        : O[K] extends Decimal
          ? string
          : O[K] extends Date
            ? string
            : O[K] extends bigint
              ? number
              : O[K];
} {
    return Object.fromEntries(
        Object.entries(object).map(([key, value]) => {
            let modifiedValue = value;

            if (Decimal.isDecimal(value)) {
                modifiedValue = value.toString();
            }

            if (value instanceof Date) {
                modifiedValue = value.toISOString();
            }

            if (typeof value === 'bigint') {
                modifiedValue = Number(value);
            }

            return [key, modifiedValue];
        }),
    ) as any;
}

/**
 * Deep clone an object using structured clone algorithm and delete properties
 *
 * !!!!! Be careful with Decimal - after transforming object decimal values not pass Decimal.isDecimal !!!!!
 *
 * @param obj Object to clone
 * @returns Cloned object
 **/
export function removeProperties<T extends Record<string, unknown>, K extends keyof T>(
    obj: T,
    ...keys: K[]
): Omit<T, K> {
    const clonedObject = rfdc()(obj);

    keys.forEach((key) => {
        delete clonedObject[key];
    });

    return clonedObject;
}

/**
 * Delete properties from an object if they are undefined or null, including nested objects
 * Arrays are preserved but their elements are processed recursively
 * Empty objects {} are also removed
 *
 * @param obj Object to delete properties from
 * @returns Object with deleted empty properties (including nested)
 */
export function removeEmptyProperties<T>(
    obj: T,
): T extends Array<any> ? T : T extends Record<string, unknown> ? Partial<T> : T {
    if (obj === null || obj === undefined) {
        return {} as any;
    }

    if (Array.isArray(obj)) {
        return obj
            .map((item) => (typeof item === 'object' ? removeEmptyProperties(item) : item))
            .filter(
                (item) =>
                    item !== null && item !== undefined && (typeof item !== 'object' || Object.keys(item).length > 0),
            ) as any;
    }

    if (typeof obj !== 'object') {
        return obj as any;
    }

    const result: Partial<Record<string, unknown>> = {};

    for (const [key, value] of Object.entries(obj)) {
        if (value === null || value === undefined) {
            continue;
        }

        if (typeof value === 'object') {
            const nestedResult = removeEmptyProperties(value);
            if (
                nestedResult !== null &&
                nestedResult !== undefined &&
                (typeof nestedResult !== 'object' || Object.keys(nestedResult).length > 0)
            ) {
                result[key] = nestedResult;
            }
        } else {
            result[key] = value;
        }
    }

    return result as any;
}
